import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import TablePagination from "@mui/material/TablePagination";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";

const STATUS_STYLES = {
  paid: { bg: "success.main", color: "#fff", label: "Paid" },
  partial: { bg: "warning.main", color: "#fff", label: "Partial" },
  owed: { bg: "error.main", color: "#fff", label: "Owed" },
  upcoming: { bg: "grey.200", color: "text.secondary", label: "Upcoming" },
  "n/a": { bg: "grey.100", color: "text.disabled", label: "Not yet a member" },
};

const money = (value) =>
  `GHS ${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

function MonthCell({ month }) {
  const style = STATUS_STYLES[month.status] || STATUS_STYLES.upcoming;
  return (
    <Tooltip
      title={`${month.label}: ${style.label}${month.amount > 0 ? ` — ${money(month.amount)}` : ""}`}
    >
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: 1,
          bgcolor: style.bg,
          color: style.color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 11,
          fontWeight: 700,
          mx: "auto",
        }}
      >
        {month.label.charAt(0)}
      </Box>
    </Tooltip>
  );
}

MonthCell.propTypes = {
  month: PropTypes.shape({
    label: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    amount: PropTypes.number,
  }).isRequired,
};

function Legend() {
  return (
    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap mb={2} data-print-hide="true">
      {Object.entries(STATUS_STYLES).map(([key, style]) => (
        <Stack key={key} direction="row" alignItems="center" spacing={0.5}>
          <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: style.bg }} />
          <Typography variant="caption" color="text.secondary">
            {style.label}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

function MemberLedgerBook({
  rows,
  meta,
  isLoading,
  error,
  onMemberClick,
  pagination,
  onPageChange,
  search,
  onSearchChange,
}) {
  const showSearch = typeof onSearchChange === "function";

  return (
    <Box>
      {showSearch && (
        <Box display="flex" justifyContent="flex-end" mb={2} data-print-hide="true">
          <TextField
            placeholder="Search member by name..."
            size="small"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            sx={{ width: { xs: "100%", sm: 260 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "text.disabled" }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      )}

      {isLoading ? (
        <Typography sx={{ py: 4 }}>Loading member book...</Typography>
      ) : error ? (
        <Typography color="error" sx={{ py: 4 }}>
          Error: {error}
        </Typography>
      ) : !rows || rows.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 4 }}>
          {search ? `No members matching "${search}".` : "No active members found."}
        </Typography>
      ) : (
        <>
          <Legend />
          {meta?.welfare_amount > 0 && (
            <Typography variant="caption" color="text.secondary" display="block" mb={2}>
              Expected welfare amount: {money(meta.welfare_amount)} / member / month.
            </Typography>
          )}
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 760 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Member</TableCell>
                  {(rows[0]?.months || []).map((m) => (
                    <TableCell key={m.month} align="center" sx={{ px: 0.5 }}>
                      {m.label}
                    </TableCell>
                  ))}
                  <TableCell align="center">Paid</TableCell>
                  <TableCell align="center">Owed</TableCell>
                  <TableCell align="right">Total Paid</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={row.member_id}
                    hover
                    onClick={() => onMemberClick(row)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Avatar sx={{ width: 28, height: 28, fontSize: 13 }}>
                          {row.name?.charAt(0)}
                        </Avatar>
                        <Typography variant="button" fontWeight={600}>
                          {row.name}
                        </Typography>
                      </Stack>
                    </TableCell>
                    {row.months.map((m) => (
                      <TableCell key={m.month} align="center" sx={{ px: 0.5 }}>
                        <MonthCell month={m} />
                      </TableCell>
                    ))}
                    <TableCell align="center">{row.paid_count}</TableCell>
                    <TableCell align="center">
                      {row.owed_count > 0 ? (
                        <Chip size="small" color="error" label={row.owed_count} />
                      ) : (
                        row.owed_count
                      )}
                    </TableCell>
                    <TableCell align="right">{money(row.total_paid)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          {pagination && (
            <TablePagination
              component="div"
              data-print-hide="true"
              count={pagination.total}
              page={pagination.current_page - 1}
              rowsPerPage={pagination.per_page}
              rowsPerPageOptions={[pagination.per_page]}
              onPageChange={(_, newPage) => onPageChange(newPage + 1)}
              sx={{ border: "none" }}
            />
          )}
        </>
      )}
    </Box>
  );
}

MemberLedgerBook.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      member_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string,
      months: PropTypes.array,
      paid_count: PropTypes.number,
      owed_count: PropTypes.number,
      total_paid: PropTypes.number,
    })
  ),
  meta: PropTypes.shape({
    welfare_amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  onMemberClick: PropTypes.func.isRequired,
  pagination: PropTypes.shape({
    current_page: PropTypes.number,
    last_page: PropTypes.number,
    per_page: PropTypes.number,
    total: PropTypes.number,
  }),
  onPageChange: PropTypes.func,
  search: PropTypes.string,
  onSearchChange: PropTypes.func,
};

MemberLedgerBook.defaultProps = {
  pagination: null,
  onPageChange: () => {},
  search: "",
  onSearchChange: null,
};

export default MemberLedgerBook;
